/**
 * Permission utility functions for checking user permissions
 */

import { PERMISSION_CATEGORIES } from '../../permissions.constants';

/** Backend may send this role with different casing (e.g. SUPERADMIN). */
export function isSuperadminRole(role?: string | null): boolean {
  if (role == null || role === '') return false;
  return String(role).trim().toLowerCase() === 'superadmin';
}

/** Client-side alias map (subset of backend LEGACY_PERMISSION_ALIASES + reverse patterns) */
const GRANULAR_ALIASES: Record<string, string[]> = {
  'order-statistics:view': ['order-statistics:read', 'user-orders:read', 'orders:read'],
  'pending-payment:view': ['pending-payment:read'],
  'pending-payment:confirm-cancel': ['pending-payment:write', 'orders:write', 'user-orders:write'],
  'order-questions:view': ['order-questions:read', 'orders:inquiry'],
  'order-questions:edit': ['order-questions:write', 'orders:inquiry'],
  'order-questions:process': ['order-questions:write', 'orders:inquiry'],
  'user-orders:read': [],
  'user-orders:write': [],
  'orders:read': [],
  'orders:write': [],
  'orders:inquiry': [],
  'company-mall:view': ['company-mall:read', 'products:read'],
  'company-mall:edit': ['company-mall:write', 'products:write'],
  'live-products:view': ['company-mall:read', 'products:read', 'live-sellers:read'],
  'live-products:edit': ['company-mall:write', 'products:write', 'live-sellers:write'],
  'live-products:delete': ['company-mall:write', 'products:delete'],
  'category-management:view': ['category-management:read', 'categories:read'],
  'category-management:edit': ['category-management:write', 'categories:write'],
  'inspection-list:view': ['inspection-list:read', 'packages:read'],
  'rack-management:view': ['rack-management:read', 'racks:read'],
  'rack-management:edit': ['rack-management:write', 'racks:write'],
  'warehouse-address:view': ['warehouse-address:read', 'warehouses:read'],
  'warehouse-address:edit': ['warehouse-address:write', 'warehouses:write'],
  'general-members:view': ['general-members:read', 'users:read'],
  'general-members:edit': ['general-members:write', 'users:write'],
  'financial-records:view': ['financial-records:read', 'settlement-management:read'],
  'deposit-application-history:view': ['member-recharge-review:read', 'deposit:read'],
  'deposit-application-history:confirm-cancel': ['member-recharge-review:write', 'deposit:write'],
  'deposit-usage-history:view': ['member-withdrawal-review:read'],
  'deposit-usage-history:excel-download': ['member-withdrawal-review:write'],
  'deposit-usage-history:accrue-deduct': [],
  'inquiry:view': ['inquiry:read', 'general:inquiry'],
  'inquiry:edit': ['inquiry:write', 'general:inquiry'],
  'administrators:view': ['administrators:read', 'admins:read'],
  'administrators:edit': ['administrators:write', 'admins:write'],
};

function orderStatusAliases(code: string): string[] {
  if (!code.startsWith('order-status:')) return [];
  if (code.endsWith(':view')) return ['user-orders:read', 'orders:read'];
  return ['user-orders:write', 'orders:write'];
}

/**
 * Check permission with granular + legacy alias support (mirrors backend permissionSatisfied).
 */
export function hasGranularPermission(
  userPermissions: string[] | undefined,
  required: string,
): boolean {
  if (!userPermissions?.length) return false;
  if (userPermissions.includes(required)) return true;

  const forward = GRANULAR_ALIASES[required] || orderStatusAliases(required);
  if (forward.some((legacy) => userPermissions.includes(legacy))) return true;

  for (const [granular, legacyList] of Object.entries(GRANULAR_ALIASES)) {
    if (legacyList.includes(required) && userPermissions.includes(granular)) return true;
  }
  for (const perm of userPermissions) {
    if (!perm.startsWith('order-status:')) continue;
    const legacy = orderStatusAliases(perm);
    if (legacy.includes(required)) return true;
  }
  return false;
}

/**
 * Check view access for a granular page resource (permission matrix row id).
 * Uses `:view` codes and legacy `:read` aliases only — no cross-category fallbacks.
 */
export function canViewPageResource(
  userPermissions: string[] | undefined,
  resource: string,
): boolean {
  if (!userPermissions?.length) return false;
  return (
    hasGranularPermission(userPermissions, `${resource}:view`) ||
    userPermissions.includes(`${resource}:read`)
  );
}

/**
 * Check if user has a specific permission
 * @param userPermissions - Array of backend permission codes from user
 * @param requiredPermission - Backend permission code to check (e.g., 'orders:read', 'products:write')
 * @returns boolean
 */
export function hasPermission(
  userPermissions: string[] | undefined,
  requiredPermission: string
): boolean {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }
  return userPermissions.includes(requiredPermission);
}

/**
 * Check if user has any of the required permissions
 * @param userPermissions - Array of backend permission codes from user
 * @param requiredPermissions - Array of backend permission codes to check
 * @returns boolean
 */
export function hasAnyPermission(
  userPermissions: string[] | undefined,
  requiredPermissions: string[]
): boolean {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }
  return requiredPermissions.some((perm) => hasGranularPermission(userPermissions, perm));
}

/**
 * Check if user has all of the required permissions
 * @param userPermissions - Array of backend permission codes from user
 * @param requiredPermissions - Array of backend permission codes to check
 * @returns boolean
 */
export function hasAllPermissions(
  userPermissions: string[] | undefined,
  requiredPermissions: string[]
): boolean {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }
  return requiredPermissions.every(perm => userPermissions.includes(perm));
}

/**
 * Permission mappings for routes/pages
 * Maps route names to required backend permission codes
 */
export const ROUTE_PERMISSIONS: Record<string, string | string[]> = {
  // Products
  'products': 'products:read',
  'products.list': 'products:read',
  'products.add': 'products:write',
  'products.categories': 'categories:read',
  'products.import-taobao': 'products:sync',
  
  // Orders
  'orders': 'orders:read',
  'orders.user': 'orders:read',
  'orders.business': 'orders:read',
  'orders.vvic': 'orders:read',
  'orders.rocket': 'orders:read',
  'orders.pending-payment': 'orders:read',
  'orders.pending-address': 'orders:read',
  'orders.in-china-warehouse': 'orders:read',
  'orders.in-korea-delivery': 'orders:read',
  'orders.completed': 'orders:read',
  'orders.no-data': 'orders:read',
  'orders.questions': 'orders:inquiry',
  'orders.inventory-questions': 'orders:inquiry',
  
  // Members
  'members': 'users:read',
  'members.general': 'users:read',
  'members.business': 'users:read',
  'members.blacklist': 'users:read',
  'members.coupons': 'users:read',
  
  // Settlement
  'settlement': ['settlement:read', 'orders:read'], // Settlement uses orders:read
  'settlement.sellers': ['settlement:read', 'orders:read'],
  'settlement.platform': ['settlement:read', 'orders:read'],
  'settlement.coupons': ['settlement:read', 'orders:read'],
  'settlement.product-price-increase': ['settlement:read', 'orders:read'],
  'settlement.service-fee': ['settlement:read', 'orders:read'],
  
  // Storehouse
  'storehouse': 'warehouses:read',
  'storehouse.express-inspection': 'warehouses:read',
  'storehouse.non-master-courier': 'warehouses:read',
  'storehouse.inspection-list': 'warehouses:read',
  'storehouse.rack': 'racks:read',
  'storehouse.warehouse-address': 'warehouses:read',
  
  // Logistics
  'logistics': 'packages:read',
  'logistics.batch-tracking': 'packages:read',
  'logistics.forwarders': 'packages:read',
  'logistics.waybill-informations': 'packages:read',
  'logistics.transportation-methods': 'packages:read',
  
  // Customer Service
  'cs': 'help-center:read',
  'cs.inquiries': 'general:inquiry',
  'cs.notices': 'help-center:read',
  'cs.faq': 'help-center:read',
  'cs.help': 'help-center:read',
  'cs.banners': 'banners:read',
  'cs.looking-for-owner': 'content:write',
  'cs.reviews': 'help-center:read',
  
  // System
  'system': 'admins:read',
  'system.admins': 'admins:read',
  'system.roles': 'permissions:manage',
  'system.rates': 'settings:read',
  'system.settings': 'settings:read',
};

/**
 * Get required permissions for a route
 * @param routeName - Route name (e.g., 'orders.user', 'settlement.sellers')
 * @returns Array of required permission codes
 */
export function getRoutePermissions(routeName: string): string[] {
  const permissions = ROUTE_PERMISSIONS[routeName];
  if (!permissions) {
    return [];
  }
  return Array.isArray(permissions) ? permissions : [permissions];
}

/**
 * Check if user can access a route
 * @param userPermissions - Array of backend permission codes from user
 * @param routeName - Route name to check
 * @returns boolean
 */
export function canAccessRoute(
  userPermissions: string[] | undefined,
  routeName: string
): boolean {
  const requiredPermissions = getRoutePermissions(routeName);
  if (requiredPermissions.length === 0) {
    // If no permissions defined, allow access (for backward compatibility)
    return true;
  }
  return hasAnyPermission(userPermissions, requiredPermissions);
}

/**
 * Check if user can perform write/edit operations on a route
 * @param userPermissions - Array of backend permission codes from user
 * @param categoryId - Permission category ID (e.g., 'orders', 'products', 'settlement')
 * @returns boolean
 */
export function canEdit(
  userPermissions: string[] | undefined,
  categoryId: string
): boolean {
  // Find the category
  const category = PERMISSION_CATEGORIES.find(cat => cat.id === categoryId);
  if (!category) {
    return false;
  }
  
  // Special case: settlement uses orders:write permission
  if (categoryId === 'settlement') {
    return hasAnyPermission(userPermissions, ['settlement:write', 'orders:write']);
  }
  
  // Check if user has write permission for this category
  // Look for write, edit, or process permissions
  const writePermissions = category.permissions.filter(p => 
    p.id.includes(':write') || 
    p.id.includes(':edit') || 
    p.id.includes(':process') ||
    p.code.includes(':write')
  );
  
  if (writePermissions.length === 0) {
    return false;
  }
  
  // Check if user has any write permission
  return writePermissions.some(p => hasPermission(userPermissions, p.code));
}

/**
 * Check if user can view a category
 * @param userPermissions - Array of backend permission codes from user
 * @param categoryId - Permission category ID (e.g., 'orders', 'products', 'settlement')
 * @returns boolean
 */
export function canView(
  userPermissions: string[] | undefined,
  categoryId: string
): boolean {
  // Find the category
  const category = PERMISSION_CATEGORIES.find(cat => cat.id === categoryId);
  if (!category) {
    return false;
  }
  
  // Special case: settlement uses orders:read permission
  if (categoryId === 'settlement') {
    return hasAnyPermission(userPermissions, ['settlement:read', 'orders:read']);
  }
  
  // Check if user has read permission for this category
  const readPermission = category.permissions.find(p => p.id.includes(':read'));
  
  if (!readPermission) {
    // If no read permission, check for any permission in the category
    return category.permissions.some(p => 
      hasPermission(userPermissions, p.code)
    );
  }
  
  return hasPermission(userPermissions, readPermission.code);
}
